import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Table, InputGroup, Alert } from 'react-bootstrap';
import {FaPlus, FaTrash, FaRoute, FaInfoCircle, FaArrowDown} from 'react-icons/fa';
import { formatNumber } from '../../../utils/formatNumber';

const Step4Yonalishlar = ({ formData, updateFormData, options }) => {
  const [fromDavlat, setFromDavlat] = useState('');
  const [toDavlat, setToDavlat] = useState('');
  const [errors, setErrors] = useState({});
  const [isCustom, setIsCustom] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Extract unique countries from available yonalishlar
  useEffect(() => {
    if (options.yonalishlar && options.yonalishlar.length > 0) {
      const countries = new Set();
      options.yonalishlar.forEach(y => {
        countries.add(y.from_davlat);
        countries.add(y.to_davlat);
      });
      setAvailableCountries(Array.from(countries).sort());
    }
  }, [options.yonalishlar]);

  const addYonalish = (from, to) => {
    // Check if this yonalish already exists
    const yonalishExists = formData.putyofka_yonalishs?.some(yo =>
        yo.from_davlat === from.trim() && yo.to_davlat === to.trim()
    );

    if (yonalishExists) {
      setErrors(prev => ({...prev, general: 'Bu yo\'nalish allaqachon qo\'shilgan'}));
      return false;
    }

    const newYonalish = {
      from_davlat: from.trim(),
      to_davlat: to.trim(),
      id: Date.now() // Temporary ID for React key
    };

    // Add to form data
    updateFormData(prev => ({
      ...prev,
      putyofka_yonalishs: [...(prev.putyofka_yonalishs || []), newYonalish]
    }));

    return true;
  };

  const handleAddYonalish = () => {
    const newErrors = {};
    const from = isCustom ? customFrom : fromDavlat;
    const to = isCustom ? customTo : toDavlat;

    if (!from.trim()) {
      newErrors.from = 'Qayerdan maydonini to\'ldiring';
    }

    if (!to.trim()) {
      newErrors.to = 'Qayerga maydonini to\'ldiring';
    }

    if (from.trim() === to.trim()) {
      newErrors.to = 'Qayerga maydoni Qayerdan maydonidan farq qilishi kerak';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (addYonalish(from, to)) {
      // Reset form only if adding was successful
      setCustomFrom('');
      setCustomTo('');
      setFromDavlat('');
      setToDavlat('');
      setErrors({});
    }
  };

  // Handle selection change for non-custom mode
  const handleSelectionChange = () => {
    if (fromDavlat && toDavlat && !isCustom) {
      addYonalish(fromDavlat, toDavlat);
      // Reset selections after adding
      setFromDavlat('');
      setToDavlat('');
    }
  };

  // Auto-add when both countries are selected in non-custom mode
  useEffect(() => {
    if (fromDavlat && toDavlat && !isCustom) {
      handleSelectionChange();
    }
  }, [fromDavlat, toDavlat, isCustom]);

  const handleRemoveYonalish = (index) => {
    const updatedYonalishlar = [...formData.putyofka_yonalishs];
    updatedYonalishlar.splice(index, 1);

    updateFormData({
      putyofka_yonalishs: updatedYonalishlar
    });
  };

  const handleFromDavlatSelect = (e) => {
    const value = e.target.value;
    setFromDavlat(value);
    setErrors(prev => ({...prev, from: undefined, general: undefined}));
  };

  const handleToDavlatSelect = (e) => {
    const value = e.target.value;
    setToDavlat(value);
    setErrors(prev => ({...prev, to: undefined, general: undefined}));
  };

  const handleToggleCustom = () => {
    const newIsCustom = !isCustom;
    setIsCustom(newIsCustom);
    setFromDavlat('');
    setToDavlat('');
    setCustomFrom('');
    setCustomTo('');
    setErrors({});
  };

  return (
      <>
        <div className="pw-form">
          <h5 className="mb-4">Yo'nalishlar</h5>

          <Row className="mb-4">
            <Col md={10}>
              <div className="mb-3">
                <Button
                    variant={isCustom ? 'outline-secondary' : 'secondary'}
                    size="sm"
                    className="me-2"
                    onClick={() => setIsCustom(false)}
                >
                  Tanlash
                </Button>
                <Button
                    variant={isCustom ? 'secondary' : 'outline-secondary'}
                    size="sm"
                    onClick={handleToggleCustom}
                >
                  Qo'lda kiritish
                </Button>
              </div>

              <Row>
                <Col md={isCustom ? 5 : 5}>
                  {isCustom ? (
                      <Form.Group className="mb-3">
                        <Form.Label>Qayerdan <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="text"
                            value={customFrom}
                            onChange={(e) => {
                              setCustomFrom(e.target.value);
                              setErrors(prev => ({...prev, from: undefined, general: undefined}));
                            }}
                            isInvalid={!!errors.from}
                            placeholder="Mamlakat nomi"
                        />
                        <Form.Control.Feedback type="invalid">{errors.from}</Form.Control.Feedback>
                      </Form.Group>
                  ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>Qayerdan <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                            value={fromDavlat}
                            onChange={handleFromDavlatSelect}
                            isInvalid={!!errors.from}
                        >
                          <option value="">Tanlang</option>
                          {availableCountries.map(country => (
                              <option key={`from-${country}`} value={country}>
                                {country}
                              </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.from}</Form.Control.Feedback>
                      </Form.Group>
                  )}
                </Col>

                <Col md={isCustom ? 5 : 5}>
                  {isCustom ? (
                      <Form.Group className="mb-3">
                        <Form.Label>Qayerga <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="text"
                            value={customTo}
                            onChange={(e) => {
                              setCustomTo(e.target.value);
                              setErrors(prev => ({...prev, to: undefined, general: undefined}));
                            }}
                            isInvalid={!!errors.to}
                            placeholder="Mamlakat nomi"
                        />
                        <Form.Control.Feedback type="invalid">{errors.to}</Form.Control.Feedback>
                      </Form.Group>
                  ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>Qayerga <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                            value={toDavlat}
                            onChange={handleToDavlatSelect}
                            isInvalid={!!errors.to}
                            disabled={!fromDavlat}
                        >
                          <option value="">Tanlang</option>
                          {availableCountries
                              .filter(country => country !== fromDavlat)
                              .map(country => (
                                  <option key={`to-${country}`} value={country}>
                                    {country}
                                  </option>
                              ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.to}</Form.Control.Feedback>
                      </Form.Group>
                  )}
                </Col>

                {isCustom && (
                  <Col md={2} className="d-flex align-items-end mb-3">
                    {/*<Button*/}
                    {/*    variant="primary"*/}
                    {/*    onClick={handleAddYonalish}*/}
                    {/*    className="w-100"*/}
                    {/*    disabled={!customFrom.trim() || !customTo.trim()}*/}
                    {/*>*/}
                    {/*  <FaPlus className="me-1" /> Qo'shish*/}
                    {/*</Button>*/}
                    <Button
                        variant="outline-primary"
                        onClick={handleAddYonalish}
                        disabled={!customFrom.trim() || !customTo.trim()}
                    >
                      <FaArrowDown className="me-1" /> Qo'shish
                    </Button>
                  </Col>
                )}
              </Row>

              {errors.general && (
                  <Alert variant="danger" className="mt-2">
                    {errors.general}
                  </Alert>
              )}

              {/*{!isCustom && (*/}
              {/*  <Button*/}
              {/*      variant="primary"*/}
              {/*      className="mt-2"*/}
              {/*      onClick={handleAddYonalish}*/}
              {/*      disabled={!fromDavlat || !toDavlat}*/}
              {/*  >*/}
              {/*    <FaPlus className="me-1"/> Qo'shish*/}
              {/*  </Button>*/}
              {/*)}*/}
            </Col>
          </Row>
        </div>

        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Qo'shilgan yo'nalishlar</h5>
            <div className="text-muted">
              Jami: <strong>{formData.putyofka_yonalishs?.length || 0} ta</strong>
            </div>
          </div>

          {formData.putyofka_yonalishs?.length > 0 ? (
              <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                  <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Qayerdan</th>
                    <th>Qayerga</th>
                    <th width="100">Harakatlar</th>
                  </tr>
                  </thead>
                  <tbody>
                  {formData.putyofka_yonalishs.map((yonalish, index) => (
                      <tr key={yonalish.id || index}>
                        <td>{index + 1}</td>
                        <td>{yonalish.from_davlat}</td>
                        <td>{yonalish.to_davlat}</td>
                        <td className="text-center">
                          <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveYonalish(index)}
                              title="O'chirish"
                          >
                            <FaTrash/>
                          </Button>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                  <tfoot>
                  {/*<tr>*/}
                  {/*  <td colSpan="3" className="text-end fw-bold">Jami:</td>*/}
                  {/*  <td className="text-end fw-bold text-primary">*/}
                  {/*    {formatNumber(formData.putyofka_yonalishs.reduce((sum, y) => sum + (parseFloat(y.narx) || 0), 0))} so'm*/}
                  {/*  </td>*/}
                  {/*  <td></td>*/}
                  {/*</tr>*/}
                  </tfoot>
                </Table>
              </div>
          ) : (
              <Alert variant="light" className="text-center py-4">
                <FaRoute size={32} className="mb-3 text-muted"/>
                <h5>Yo'nalishlar mavjud emas</h5>
                <p className="mb-0 text-muted">Yuqoridagi maydonlardan yo'nalish qo'shing</p>
              </Alert>
          )}

        {/*  <Alert variant="info" className="mt-4">*/}
        {/*    <FaInfoCircle className="me-2"/>*/}
        {/*    <span>*/}
        {/*      Quyidagi jadvalda tanlangan yo'nalishlar*/}
        {/*</span>*/}
        {/*  </Alert>*/}
        </div>
        <div className="d-flex justify-content-between align-items-center mt-4 p-3 bg-light rounded">
          <div>
            {/*<h6 className="mb-1">Jami narx:</h6>*/}
            {/*<p className="h4 text-primary mb-0">*/}
            {/*  {formatNumber(formData.putyofka_yonalishs?.reduce((sum, y) => sum + (parseFloat(y.narx) || 0), 0) || 0)} so'm*/}
            {/*</p>*/}
          </div>
          <div className="text-end">
            <p className="mb-1">Jami yo'nalishlar: <strong>{formData.putyofka_yonalishs?.length || 0} ta</strong></p>
            <p className="mb-0">
              <FaRoute className="text-muted me-1"/>
              {formData.putyofka_yonalishs?.map(y => `${y.from_davlat}-${y.to_davlat}`).join(', ') || 'Yo\'nalishlar mavjud emas'}
            </p>
          </div>
        </div>
      </>
  );
}

export default Step4Yonalishlar;
